import React, { useState } from 'react'
import { Card, Button, Badge } from '@repo/ui-core'
import { RecurringJournalTemplate, RecurringSchedule } from '@repo/entity-accounting'

// A simple local store for demo purposes (if we want to use IndexedDB later, we can)
export function RecurringJournalPage() {
  const [templates, setTemplates] = useState<RecurringJournalTemplate[]>([
    {
      id: 'rjt-1',
      tenantId: 'default',
      templateName: 'Monthly Rent Amortization',
      schedule: 'monthly',
      nextRunDate: Date.now() - 86400000 * 5, // 5 days ago (past due)
      lines: [
        { accountCode: '6020', debitAmount: 25000, creditAmount: 0, description: 'Rent Expense' },
        { accountCode: '1300', debitAmount: 0, creditAmount: 25000, description: 'Prepaid Rent' }
      ],
      autoPost: true,
      isActive: true,
    }
  ])
  
  const [generatedCount, setGeneratedCount] = useState(0)

  const handleRunPending = () => {
    // We mock the service call here or call the actual service if we had dexie hooked up
    // AccountingService.processRecurringJournalTemplates(templates, Date.now())
    let processedCount = 0
    const now = Date.now()
    
    const updated = templates.map(t => {
      if (!t.isActive || t.nextRunDate > now) return t
      
      let currentNextRun = t.nextRunDate
      while (currentNextRun <= now) {
        processedCount++
        const d = new Date(currentNextRun)
        switch (t.schedule) {
          case 'daily': d.setDate(d.getDate() + 1); break
          case 'weekly': d.setDate(d.getDate() + 7); break
          case 'monthly': d.setMonth(d.getMonth() + 1); break
          case 'quarterly': d.setMonth(d.getMonth() + 3); break
          case 'annually': d.setFullYear(d.getFullYear() + 1); break
        }
        currentNextRun = d.getTime()
      }
      return { ...t, nextRunDate: currentNextRun, lastRunDate: now }
    })
    
    setTemplates(updated)
    setGeneratedCount(prev => prev + processedCount)
    alert(`Successfully generated ${processedCount} journal entries.`)
  }

  const handleToggleActive = (id: string) => {
    setTemplates(prev => prev.map(t => t.id === id ? { ...t, isActive: !t.isActive } : t))
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Recurring Journal Entries</h1>
          <p className="text-sm text-gray-500">Manage and automate periodic journal entries</p>
        </div>
        <Button onClick={handleRunPending} className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg">
          Run Pending Templates
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 flex flex-col items-center justify-center bg-blue-50 border border-blue-100">
          <span className="text-3xl font-bold text-blue-700">{templates.length}</span>
          <span className="text-sm text-blue-600 font-medium">Total Templates</span>
        </Card>
        <Card className="p-4 flex flex-col items-center justify-center bg-green-50 border border-green-100">
          <span className="text-3xl font-bold text-green-700">{templates.filter(t => t.isActive).length}</span>
          <span className="text-sm text-green-600 font-medium">Active</span>
        </Card>
        <Card className="p-4 flex flex-col items-center justify-center bg-amber-50 border border-amber-100">
          <span className="text-3xl font-bold text-amber-700">{templates.filter(t => t.isActive && t.nextRunDate <= Date.now()).length}</span>
          <span className="text-sm text-amber-600 font-medium">Pending Run</span>
        </Card>
        <Card className="p-4 flex flex-col items-center justify-center bg-purple-50 border border-purple-100">
          <span className="text-3xl font-bold text-purple-700">{generatedCount}</span>
          <span className="text-sm text-purple-600 font-medium">Entries Generated</span>
        </Card>
      </div>

      <Card>
        <div className="p-0 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Template Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Run</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {templates.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    No recurring templates configured.
                  </td>
                </tr>
              ) : (
                templates.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{t.templateName}</div>
                      <div className="text-sm text-gray-500">{t.lines.length} lines</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="capitalize text-sm text-gray-700">{t.schedule}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {t.isActive ? (
                        <Badge color="green">Active</Badge>
                      ) : (
                        <Badge color="gray">Inactive</Badge>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(t.nextRunDate).toLocaleDateString()}
                      {t.nextRunDate <= Date.now() && t.isActive && (
                        <span className="ml-2 text-xs text-red-600 font-medium">Past Due</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button 
                        onClick={() => handleToggleActive(t.id)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        {t.isActive ? 'Pause' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
